// Cloudflare Turnstile: the API answers only browsers that passed the check.
// Backend: POST /api/v1.0/visitor-session/ {token} -> 204 + HttpOnly cookie for 60 min;
// an /api/ request without it -> 403 {code: "visitor_required"}.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SESSION_URL = "/api/v1.0/visitor-session/";
const TURNSTILE_TIMEOUT_MS = 30000;
const FAILURE_COOLDOWN_MS = 30000; // after a failed check, fail fast instead of making every request wait again
const VERIFICATION_REQUIRED_CODE = "visitor_required";

let pending = null;
let failedUntil = 0;

const getTurnstileToken = () => new Promise((resolve, reject) => {
    const { turnstile } = window;
    if (!turnstile) {
        reject(new Error("Turnstile script is not loaded"));
        return;
    }

    // Shown only when Cloudflare asks for an interaction (appearance: interaction-only)
    const container = document.createElement("div");
    container.style.cssText = "position:fixed;right:16px;bottom:16px;z-index:2147483647";
    document.body.appendChild(container);

    let widgetId = null;
    let timer = null;
    const finish = (settle) => {
        clearTimeout(timer);
        if (widgetId) turnstile.remove(widgetId);
        container.remove();
        settle();
    };

    timer = setTimeout(() => finish(() => reject(new Error("Turnstile timeout"))), TURNSTILE_TIMEOUT_MS);
    widgetId = turnstile.render(container, {
        sitekey: SITE_KEY,
        appearance: "interaction-only",
        callback: (token) => finish(() => resolve(token)),
        // Turnstile retries by itself after an error (retry: "auto"); the timeout above caps the wait
        "error-callback": (code) => console.warn(`Turnstile error ${code}, retrying`),
    });
});

// One check at a time, however many requests are waiting for it.
// fetch, not axios: the session request must not go through the interceptor below.
export const ensureVisitorSession = () => {
    if (!SITE_KEY) return Promise.resolve();
    if (pending) return pending;
    if (Date.now() < failedUntil) return Promise.reject(new Error("Turnstile recently failed"));

    pending = getTurnstileToken()
        .then((token) => fetch(SESSION_URL, {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
        }))
        .then((response) => {
            if (!response.ok) throw new Error(`Visitor session failed: ${response.status}`);
        })
        .catch((error) => {
            failedUntil = Date.now() + FAILURE_COOLDOWN_MS;
            throw error;
        })
        .finally(() => {
            pending = null;
        });
    return pending;
};

// Requests are never held back: only a 403 visitor_required triggers the check and one retry.
export const attachVisitorVerification = (instance) => {
    instance.interceptors.response.use(undefined, async (error) => {
        const { config, response } = error;
        if (!config || config._visitorRetry || response?.data?.code !== VERIFICATION_REQUIRED_CODE) {
            throw error;
        }
        config._visitorRetry = true;
        await ensureVisitorSession().catch(() => {
            throw error;
        });
        return instance.request(config);
    });
};